/**
 * The founding waiting list (L7): once every founding place is taken, a
 * visitor on the founding link leaves an email, an Instagram name or a
 * Facebook name to hear when more places open. Needs no sign-in.
 *
 * The server reads the one field (an email by its shape, otherwise a name on
 * the network chosen, with a leading "@" or a pasted profile address taken
 * off) and answers what it kept, or 400 { code: 'invalid' } when it is
 * neither; the app only sends what was typed and the choice.
 *
 * Endpoint: POST /billing/founding-waitlist { contact, network, language } →
 * { success, kind }. Who uses it: features/landing FoundingFull.
 *
 * @module api/foundingWaitlist
 */

import { apiClient } from './apiClient';

/** The networks a name can be on, as the form offers them. */
export type FoundingWaitlistNetwork = 'instagram' | 'facebook';

/** What the server kept the contact as. */
export type FoundingWaitlistKind = 'email' | FoundingWaitlistNetwork;

/** What the form sends. */
export interface FoundingWaitlistRequest {
  /** The field as typed. */
  contact: string;
  /** Which network a name is on (the server ignores it for an email or a pasted profile address). */
  network: FoundingWaitlistNetwork;
  /** The page's language ('en' or 'es'), kept with the contact. */
  language: string;
}

/**
 * Puts a visitor on the founding waiting list, once (a repeat changes nothing).
 *
 * @param request - The field as typed, the network chosen and the page's language.
 * @returns What the contact was kept as.
 * @throws When the server refuses it (400, code 'invalid'), limits it (429) or fails (500, no answer).
 */
export async function joinFoundingWaitlist(request: FoundingWaitlistRequest): Promise<FoundingWaitlistKind> {
  const response = await apiClient.post<{ success: boolean; kind: FoundingWaitlistKind }>('/billing/founding-waitlist', request);
  return response.data.kind;
}
